
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import CourseStepper from './Stepper';
import { Box, Button, Fab, styled, Tooltip } from '@mui/material';
import { DescriptionBlock, SlidingBlock, StartCourseSharedData } from './SharedStuff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useMemo , useState, useEffect } from 'react';
import { ImageWithSkeleton } from '../../shared/components/imageLoading/ImageWithSkeleton';
import axiosInstance from '../../axios.config';
import { useParams } from "react-router-dom";


const LeftButton = styled(Fab)(({ theme }) => ({
    position: "fixed",
    left: "calc(80px + 2rem)", // Account for sidebar width (typically 80px) + additional spacing
    [theme.breakpoints.down("xl")]: {
        left: "calc(80px + 1.5rem)",
    },
    [theme.breakpoints.down("lg")]: {
        left: "calc(80px + 1rem)",
    },
    [theme.breakpoints.down("md")]: {
        left: "calc(70px + 1rem)", // Smaller sidebar on medium screens
    },
    [theme.breakpoints.down("sm")]: {
        left: "1rem", // On small screens, sidebar might be collapsed
    },
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1000,
    background: "linear-gradient(135deg, #212529 0%, #343a40 100%)",
    border: '2px solid #dc3545',
    width: '56px',
    height: '56px',
    boxShadow: '0 4px 16px rgba(220, 53, 69, 0.3)',
    transition: 'all 0.3s ease',
    "&:hover": {
        background: "linear-gradient(135deg, #343a40 0%, #495057 100%)",
        boxShadow: '0 6px 20px rgba(220, 53, 69, 0.5)',
        transform: 'translateY(-50%) scale(1.1)',
    }
}));


const RightButton = styled(Fab)(({ theme }) => ({
    position: 'fixed',
    right: "2rem",
    [theme.breakpoints.down("xl")]: {
        right: "1.5rem",
    },
    [theme.breakpoints.down("md")]: {
        right: "1rem",
    },
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1000,
    background: "linear-gradient(135deg, #212529 0%, #343a40 100%)",
    border: '2px solid #dc3545',
    width: '56px',
    height: '56px',
    boxShadow: '0 4px 16px rgba(220, 53, 69, 0.3)',
    transition: 'all 0.3s ease',
    "&:hover": {
        background: "linear-gradient(135deg, #343a40 0%, #495057 100%)",
        boxShadow: '0 6px 20px rgba(220, 53, 69, 0.5)',
        transform: 'translateY(-50%) scale(1.1)',
    }
}));

export const StartCourseOnDesktop = ({
    scrollableDivRef, coursesSteps, currentStep, stepsTitles, handlePrevious, imageUrls,
    videoUrl, setVideoWatched, lngsltd, handleNext, handleSubmission, htmlContent, bgImgUrl }) => {

    const languagecultureSelected = JSON.parse(localStorage.getItem('cultureSelected'));

    const { id: courseId } = useParams();
    const [hasQuiz, setHasQuiz] = useState(false);

    const showBackBtn = useMemo(() => currentStep > 0, [currentStep])
    const showNextBtn = useMemo(() => currentStep < coursesSteps.length - 1, [coursesSteps, currentStep])

    useEffect(() => {
    const checkQuiz = async () => {
      if (!courseId) return;

      try {
        const response = await axiosInstance.get(`/Quiz/ByCourse/${courseId}`);
        setHasQuiz(!!response?.data);
      } catch (error) {
        console.error("Error checking quiz:", error);
        setHasQuiz(false);
      }
    };

    checkQuiz();
  }, [courseId]); // ✅ only run when courseId changes

    if (languagecultureSelected) {
        let descriptionData;
        try {
            // Parse the JSON stored in the database
            descriptionData = JSON.parse(htmlContent);

            // Extract English and Norwegian descriptions
            const englishDescription = descriptionData["en"] || "";
            const norwegianDescription = descriptionData["no"] || "";

            if (languagecultureSelected === 'en-US') {
                htmlContent = englishDescription;
            }

            if (languagecultureSelected === 'nb-NO') {
                htmlContent = norwegianDescription;
            }
        }
        catch {

        }
    }

    return (
        <>
            {/* Content Section */}
            <Box 
                ref={scrollableDivRef} 
                position="relative" 
                display="flex" 
                flexDirection="column" 
                sx={{ 
                    minHeight: "calc(100% - 40px)", 
                    background: '#fafafa', 
                    overflowX: 'visible',
                    pb: { xs: '100px', sm: '90px' } // Add bottom padding to prevent content from being hidden behind fixed button
                }}
            >

                {/* Stepper */}
                <CourseStepper coursesSteps={coursesSteps} currentStep={currentStep} stepsTitles={stepsTitles}></CourseStepper>

                {/* <Box flexGrow={1} sx={{ overflowX: "hidden" }} display="flex" flexDirection="column"> */}
                    <CourseInnerContent
                        coursesSteps={coursesSteps}
                        currentStep={currentStep}
                        imageUrls={imageUrls}
                        lngsltd={lngsltd}
                        setVideoWatched={setVideoWatched}
                        videoUrl={videoUrl}
                    />
                {/* </Box> */}

                {showBackBtn ?
                    <>
                        <Tooltip title="Back">
                            <LeftButton color="primary" aria-label="back" onClick={handlePrevious}>
                                <ArrowBackIcon />
                            </LeftButton>
                        </Tooltip>
                    </> : null
                }

                {showNextBtn ?
                    <>
                        <Tooltip title="Next">
                            <RightButton color="primary" aria-label="forward" onClick={handleNext}>
                                <ArrowForwardIcon />
                            </RightButton>
                        </Tooltip>
                    </> : null
                }

                <DescriptionBlock
                    htmlContent={htmlContent}
                    bgImgUrl={bgImgUrl}
                />

                {/* Submit Button - Fixed at Bottom */}
                {coursesSteps.length > 0 && currentStep >= coursesSteps.length - 1 && (
                    <Box 
                        sx={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: '#ffffff',
                            borderTop: '1px solid #e5e7eb',
                            boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
                            zIndex: 1000,
                            py: { xs: 2.5, sm: 2, md: 2 },
                            px: { xs: 2, sm: 3, md: 4 },
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <Button
                            variant="contained"
                            sx={{ 
                                width: { xs: '90%', sm: 'auto' },
                                minWidth: { xs: '280px', sm: '300px', md: '320px' },
                                maxWidth: { xs: '100%', sm: '450px', md: '400px' },
                                height: { xs: '52px', sm: '50px', md: '48px' },
                                background: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)',
                                borderRadius: '12px',
                                fontWeight: 700,
                                fontSize: { xs: '1rem', sm: '0.95rem', md: '0.9rem' },
                                textTransform: 'none',
                                boxShadow: '0 6px 20px rgba(220, 53, 69, 0.35)',
                                transition: 'all 0.3s ease',
                                letterSpacing: '0.02em',
                                px: { xs: 4, sm: 3, md: 3 },
                                py: { xs: 1.5, sm: 1.25, md: 1.25 },
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #c82333 0%, #bd2130 100%)',
                                    boxShadow: '0 8px 24px rgba(220, 53, 69, 0.45)',
                                    transform: 'translateY(-2px)',
                                }
                            }}
                            endIcon={<FontAwesomeIcon icon={faPaperPlane} />}
                            onClick={handleSubmission}
                        >                            
                            {hasQuiz
                                ? lngsltd["Let's Test Your Knowledge!"]
                                : lngsltd["Submit"]}
                        </Button>
                    </Box>
                )}
            </Box>
        </>
    );
}

const CourseInnerContent = ({ coursesSteps, currentStep, imageUrls, videoUrl, setVideoWatched, lngsltd }) => {
    
    if (!coursesSteps[currentStep]) return <></>

    if (coursesSteps[currentStep].contentType === 'Image') {
        return (
            <>
             <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column" 
                gap={4}  
                width="100%"
                py={5}
                px={3}
                sx={{ background: '#fafafa', minHeight: '400px' }}
            >
                {imageUrls.length > 0 && (
                    <>
                        {imageUrls.map((imageUrl, index) => (
                            <Box
                                key={index}
                                sx={{
                                    width: '60%',
                                    maxWidth: '800px',
                                    borderRadius: '16px',
                                    overflow: 'hidden',
                                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                                    background: '#fff',
                                    border: '2px solid #e0e0e0',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.18)',
                                        transform: 'translateY(-4px)',
                                    }
                                }}
                            >
                                <ImageWithSkeleton
                                    height="auto"
                                    width='100%'
                                    altText="Image could not be loaded"
                                    animationDuration=""
                                    imageUrl={imageUrl}
                                    slideTo={StartCourseSharedData.getSlideTo()}
                                    slidingKey={currentStep}
                                />
                            </Box>
                        ))}
                    </>
                )}
                </Box>
            </>
        )
    } else if (coursesSteps[currentStep].contentType === 'Video') {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="100%"
                py={5}
                px={3}
                sx={{ background: '#fafafa', minHeight: '400px' }}
            >
                <Box
                    sx={{
                        width: '60%',
                        maxWidth: '900px',
                        aspectRatio: '16/9',
                        backgroundColor: '#000',
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: '16px',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                        border: '2px solid #212529',
                    }}
                >
                    {videoUrl ? (
                        <SlidingBlock currentStep={currentStep}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    minWidth: '100%',
                                    minHeight: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                }}
                            >
                                <video
                                    src={videoUrl}
                                    controls      
                                    autoPlay                              
                                    preload="metadata"
                                    muted                                                                                                                                            
                                    style={{ 
                                        width: '100%',
                                        height: '100%',
                                        minWidth: '100%',
                                        minHeight: '100%',
                                        backgroundColor: '#000',
                                        objectFit: 'contain',
                                        objectPosition: 'center',
                                        display: 'block',
                                        margin: 0,
                                        padding: 0
                                    }}
                                    onEnded={() => setVideoWatched(true)}
                                    onError={(e) => {
                                        const video = e.target;
                                        const error = video.error;
                                        console.error('Video error:', error?.code, error?.message);
                                    }}
                                    onLoadedData={(e) => {
                                        // Force video to fill container on load
                                        const video = e.target;
                                        video.style.width = '100%';
                                        video.style.height = '100%';
                                    }}
                                    onCanPlay={(e) => {
                                        // Ensure proper sizing when video is ready
                                        const video = e.target;
                                        video.style.width = '100%';
                                        video.style.height = '100%';
                                    }}
                                >
                                    {lngsltd["Your browser does not support the video tag"]}
                                </video>
                            </Box>
                        </SlidingBlock>
                    ) : (
                        <Box 
                            sx={{ 
                                color: '#fff',
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)'
                            }}
                        >
                            <b>{lngsltd["Loading...Please wait"]}</b>
                        </Box>
                    )}
                </Box>
            </Box>
        )
    }
}
